import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import {
  copyFile,
  getEntity,
  getPage,
  getWorkspace,
  linkTaskFile,
  normalizeError,
  unlinkTaskFile,
  uploadFile,
  uploadRevision,
} from "./api";
import { resolveLevelFile } from "./selectors";

/**
 * THE WORKSPACE SERVER CACHE (FE1 plan 4.2). Session-only, NOT persisted.
 *
 * ONE CURRENT CONTEXT. `current` names the context key
 * (`${userId}:${squadId}:${capsuleId}`) and a generation that increases on
 * every context change. `byKey` holds only the current key's entry: a context
 * change drops everything the previous key held, so no view can paint a
 * previous level's data and nothing is resurrected on return.
 *
 * TWO GUARDS ON EVERY RESPONSE. A fulfilled or rejected action is applied only
 * when (a) its generation and key are the current ones and (b) it is still the
 * request that owns its operation slot (`inflight[opKey] === requestId`). A
 * newer request for the SAME slot supersedes an older one; different slots are
 * independent, so a page read never cancels an entity read. A response from a
 * stale generation is dropped entirely, including a late `workspace_disabled`.
 *
 * THE ONE LEGACY SELECTOR. Only 404 `workspace_disabled` selects legacy. On a
 * first bootstrap it means `disabled` (negative-cached for the session in
 * `disabledKeys`); after the key was `enabled` it means `withdrawn`, which
 * clears every protected collection. Every other context-wide failure is a
 * protected `error` state, never legacy.
 */

const TERMINAL_STATUSES = ["disabled", "withdrawn"];

/** Context-wide refusals: the subject of the request was the context itself. */
const CONTEXT_WIDE_CODES = ["workspace_not_found", "validation_failed"];

const isContextWide = (error) =>
  error.status === 401 ||
  error.status === 403 ||
  CONTEXT_WIDE_CODES.includes(error.code);

const emptyCollection = () => ({ byId: {}, order: [], nextCursor: null });

/**
 * THE GOOGLE PROJECTION, ALLOWLISTED (FE2 plan 4.3). The bootstrap's `google`
 * block is a HINT the UI reads to decide what to offer; every mutation is
 * re-checked by the server. Only the fields `GoogleBootstrapService::project`
 * defines are copied, so nothing the server did not mean to project (and no
 * token, nonce or URL) can ever land in the store. Null when the backend sent
 * no block, which is what every flag-off context answers.
 */
const pick = (source, fields) => {
  const out = {};
  fields.forEach((field) => {
    out[field] = source?.[field] ?? null;
  });
  return out;
};

/* BE3.3: the Google kinds in canonical order, and the capture formats. */
const GOOGLE_KINDS = ["document", "spreadsheet", "presentation"];
const CAPTURE_FORMATS = ["docx", "pdf", "pptx"];

/** The enabled kinds, canonical order, or null (absent: Docs only). */
const sanitizeFileKinds = (raw) => {
  if (!Array.isArray(raw)) return null;
  const kinds = GOOGLE_KINDS.filter((kind) => raw.includes(kind));
  return kinds.length ? kinds : null;
};

/** `{ kind: [formats, default first] }` for known kinds, or null. */
const sanitizeCaptureFormats = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const out = {};
  GOOGLE_KINDS.forEach((kind) => {
    const formats = Array.isArray(raw[kind])
      ? raw[kind].filter((format) => CAPTURE_FORMATS.includes(format))
      : [];
    if (formats.length) out[kind] = formats;
  });
  return Object.keys(out).length ? out : null;
};

const sanitizeGoogle = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const connection = pick(raw.connection, [
    "status",
    "email",
    "scope_ready",
    "generation",
  ]);
  connection.status = connection.status ?? "disconnected";
  connection.scope_ready = connection.scope_ready === true;
  connection.generation = Number(connection.generation) || 0;
  const membership = pick(raw.membership, ["join_consented", "sharing_status"]);
  membership.join_consented = membership.join_consented === true;
  const currentLevel = pick(raw.current_level, [
    "google_level_folder_id",
    "authorization",
    "can_create_document",
    "checked_at",
  ]);
  currentLevel.can_create_document = currentLevel.can_create_document === true;
  /* BE3.4: present only with the backend's squad-copy flag on. Absent, no
     key is added, so a flag-off block is stored exactly as before. */
  if (raw.current_level?.attach_mode === "squad_copy") {
    currentLevel.attach_mode = "squad_copy";
  }
  /* BE3.3: present only when the backend widened a kinds allowlist. Absent,
     no key is added, so a flag-off block is stored exactly as before. */
  const fileKinds = sanitizeFileKinds(raw.file_kinds);
  const captureFormats = sanitizeCaptureFormats(raw.capture_formats);
  return {
    ...(fileKinds ? { file_kinds: fileKinds } : {}),
    ...(captureFormats ? { capture_formats: captureFormats } : {}),
    connection,
    setup: pick(raw.setup, [
      "root_id",
      "status",
      "initializer_user_id",
      "operation_id",
      "membership_version",
    ]),
    membership,
    current_level: currentLevel,
    available_actions: Array.isArray(raw.available_actions)
      ? raw.available_actions.filter((action) => typeof action === "string")
      : [],
  };
};

const freshEntry = (status = "probing") => ({
  status,
  error: null,
  validatedAt: null,
  lastValidationAttemptAt: null,
  bootstrapStartedAt: null,
  consecutiveFailures: 0,
  context: null,
  capabilities: null,
  google: null,
  outputs: [],
  submission: null,
  tasks: {
    ...emptyCollection(),
    complete: false,
    error: null,
    autoFollowed: 0,
  },
  /* `retained`: file ids kept on tasks only because an unloaded references
     page may still supply them (reconciled when references complete). */
  files: {
    ...emptyCollection(),
    error: null,
    hydrationErrors: {},
    retained: {},
  },
  references: { ...emptyCollection(), error: null },
  earlier: {},
  inflight: {},
  /* S4 D-3: per entity slot, the id of the last read the store APPLIED. A
     link is confirmed only from a read whose own id is recorded here. */
  appliedReads: {},
  /**
   * FE3 plan 4.3: THE ACTIVE CAPTURE for this context, or null.
   * `{ captureId, idempotencyKey, fingerprint, expectedSubmissionVersion,
   * items }`. Kept here, not in the drawer, so closing the drawer dismisses
   * the UI without abandoning a capture that is still preparing: reopening
   * Ship re-reads its status. It is an identifier and a key, never artifact
   * authority, and it goes with the context like everything else here.
   */
  capture: null,
});

/**
 * A task link kept while references were unloaded is settled by the
 * references themselves: once a loaded reference supplies the id, its
 * hydration error is cleared and the link stands; once the LAST page has
 * loaded without it, the id is gone from this level and is removed from every
 * task, exactly as a vanished file is.
 */
const reconcileRetained = (entry) => {
  const complete = entry.references.nextCursor === null;
  Object.keys(entry.files.retained).forEach((key) => {
    const id = Number(key);
    if (resolveLevelFile(entry, id)) {
      delete entry.files.retained[key];
      delete entry.files.hydrationErrors[id];
      return;
    }
    if (!complete) return;
    delete entry.files.retained[key];
    delete entry.files.hydrationErrors[id];
    Object.values(entry.tasks.byId).forEach((task) => {
      if (!Array.isArray(task.file_ids)) return;
      task.file_ids = task.file_ids.filter((fileId) => Number(fileId) !== id);
    });
  });
};

const initialState = {
  current: { contextKey: null, generation: 0 },
  byKey: {},
  disabledKeys: {},
};

/** Insert or replace by id, keeping `order` id-descending like the server. */
const upsert = (collection, item) => {
  const id = Number(item.id);
  collection.byId[id] = { ...item, id };
  if (collection.order.includes(id)) return;
  const at = collection.order.findIndex((existing) => existing < id);
  if (at === -1) collection.order.push(id);
  else collection.order.splice(at, 0, id);
};

const removeFrom = (collection, id) => {
  delete collection.byId[id];
  collection.order = collection.order.filter((existing) => existing !== id);
};

const mergePage = (collection, items) => {
  (Array.isArray(items) ? items : []).forEach((item) =>
    upsert(collection, item),
  );
};

const clearProtected = (entry) => {
  entry.context = null;
  entry.capabilities = null;
  entry.google = null;
  entry.outputs = [];
  entry.submission = null;
  entry.tasks = freshEntry().tasks;
  entry.files = freshEntry().files;
  entry.references = freshEntry().references;
  entry.earlier = {};
  entry.inflight = {};
  entry.appliedReads = {};
  entry.capture = null;
};

const withdraw = (state, contextKey, entry) => {
  clearProtected(entry);
  entry.status = "withdrawn";
  entry.error = null;
  state.disabledKeys[contextKey] = true;
};

const protect = (entry, error) => {
  clearProtected(entry);
  entry.status = "error";
  entry.error = { ...error, kind: "context" };
};

/**
 * The entry this action may write to, or null.
 *
 * Null when the action belongs to an earlier generation or another key, or
 * when a newer request now owns its operation slot.
 */
const owningEntry = (state, action, opKey) => {
  const { contextKey, generation } = action.meta.arg;
  if (
    generation !== state.current.generation ||
    contextKey !== state.current.contextKey
  ) {
    return null;
  }
  const entry = state.byKey[contextKey];
  if (!entry || entry.inflight[opKey] !== action.meta.requestId) return null;
  delete entry.inflight[opKey];
  return entry;
};

const claimSlot = (state, action, opKey) => {
  const { contextKey, generation } = action.meta.arg;
  if (
    generation !== state.current.generation ||
    contextKey !== state.current.contextKey
  ) {
    return null;
  }
  const entry = state.byKey[contextKey];
  if (!entry || TERMINAL_STATUSES.includes(entry.status)) return null;
  entry.inflight[opKey] = action.meta.requestId;
  return entry;
};

/**
 * The shared rejection rule for any request whose subject is the context
 * (bootstrap, page reads, single-entity reads). Returns true when it decided
 * the entry's fate, so the caller only handles the local cases.
 */
const applyContextFailure = (state, action, entry) => {
  const error = action.payload ?? normalizeError(action.error);
  if (error.aborted) return true;
  if (error.code === "workspace_disabled") {
    withdraw(state, action.meta.arg.contextKey, entry);
    return true;
  }
  if (isContextWide(error)) {
    protect(entry, error);
    return true;
  }
  return false;
};

const opKeys = {
  bootstrap: () => "bootstrap",
  page: ({ collection, cursor }) => `page:${collection}:${cursor ?? "first"}`,
  entity: ({ collection, id }) => `entity:${collection}:${Number(id)}`,
  earlier: ({ earlierCapsuleId, cursor }) =>
    `earlier:${Number(earlierCapsuleId)}:${cursor ?? "first"}`,
  mutation: ({ intentId }) => `mutation:${intentId}`,
};

/**
 * The api already rejects in the normalized shape; anything else (a throw
 * inside this module) is normalized here so the reducers see one shape.
 */
const withRejection =
  (run) =>
  async (arg, { signal, rejectWithValue }) => {
    try {
      const data = await run(arg, signal);
      return { data, settledAt: Date.now() };
    } catch (error) {
      const normalized =
        error && typeof error.status === "number" && "aborted" in error
          ? error
          : normalizeError(error);
      return rejectWithValue({ ...normalized, settledAt: Date.now() });
    }
  };

/**
 * Every thunk takes `{ contextKey, generation, squadId, capsuleId, ... }`.
 * The provider supplies the first two from the store at dispatch time.
 */
export const bootstrapWorkspace = createAsyncThunk(
  "workspace/bootstrap",
  withRejection(({ squadId, capsuleId }, signal) =>
    getWorkspace({ squadId, capsuleId, signal }),
  ),
);

export const loadPage = createAsyncThunk(
  "workspace/loadPage",
  withRejection(({ collection, squadId, capsuleId, cursor, limit }, signal) =>
    getPage({ collection, squadId, capsuleId, cursor, limit, signal }),
  ),
);

export const refreshEntity = createAsyncThunk(
  "workspace/refreshEntity",
  withRejection(({ collection, squadId, capsuleId, id }, signal) =>
    getEntity({ collection, squadId, capsuleId, id, signal }),
  ),
);

/**
 * An earlier level's files. The source context is explicit: the request names
 * the EARLIER capsule, which is what `/access` will later need for the same
 * file.
 */
export const loadEarlierFiles = createAsyncThunk(
  "workspace/loadEarlierFiles",
  withRejection(({ squadId, earlierCapsuleId, cursor, limit }, signal) =>
    getPage({
      collection: "files",
      squadId,
      capsuleId: earlierCapsuleId,
      cursor,
      limit,
      signal,
    }),
  ),
);

const MUTATIONS = {
  upload: (arg, signal) =>
    uploadFile({
      squadId: arg.squadId,
      capsuleId: arg.capsuleId,
      file: arg.file,
      questId: arg.questId ?? null,
      idempotencyKey: arg.idempotencyKey,
      signal,
    }),
  revision: (arg, signal) =>
    uploadRevision({
      fileId: arg.fileId,
      squadId: arg.squadId,
      capsuleId: arg.capsuleId,
      file: arg.file,
      expectedLockVersion: arg.expectedLockVersion,
      idempotencyKey: arg.idempotencyKey,
      signal,
    }),
  /* FE3: an earlier UPLOAD reference materialized into this level. */
  copy: (arg, signal) =>
    copyFile({
      fileId: arg.fileId,
      squadId: arg.squadId,
      targetCapsuleId: arg.capsuleId,
      revisionId: arg.revisionId,
      idempotencyKey: arg.idempotencyKey,
      signal,
    }),
  link: (arg) =>
    linkTaskFile({
      questId: arg.questId,
      fileId: arg.fileId,
      squadId: arg.squadId,
      capsuleId: arg.capsuleId,
    }),
  unlink: (arg) =>
    unlinkTaskFile({
      questId: arg.questId,
      fileId: arg.fileId,
      squadId: arg.squadId,
      capsuleId: arg.capsuleId,
    }),
};

/**
 * ONE MUTATION (plan 4.5). `kind` is upload, revision, link or unlink, and
 * `intentId` names the user intent it belongs to. The fulfilled payload is an
 * OPERATION RECEIPT and no reducer writes it into the entity cache: the caller
 * reconciles every affected entity with `refreshEntity`. The file object is
 * carried in the thunk arg (`meta.arg`), which RTK's serializable check
 * ignores by default, and is never stored in state.
 */
export const runMutation = createAsyncThunk(
  "workspace/runMutation",
  withRejection((arg, signal) => MUTATIONS[arg.kind](arg, signal)),
);

const workspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    /**
     * THE ATOMIC CONTEXT SWITCH (plan 4.1). One action: the generation moves,
     * the previous key's cache is gone, and a key already known to be disabled
     * this session goes straight to `disabled` with no request.
     */
    contextActivated(state, action) {
      const contextKey = action.payload;
      state.current.generation += 1;
      state.current.contextKey = contextKey;
      state.byKey = {
        [contextKey]: freshEntry(
          state.disabledKeys[contextKey] ? "disabled" : "probing",
        ),
      };
    },
    /** No ready context: nothing is shown and every late response drops. */
    contextCleared(state) {
      state.current.generation += 1;
      state.current.contextKey = null;
      state.byKey = {};
    },
    /**
     * THE PROVIDER UNMOUNTED. Everything goes, including the negative cache:
     * a cohort enabled mid-session appears on the next Dashboard mount (plan
     * 4.8), and nothing a previous mount held is resurrected.
     */
    sessionEnded(state) {
      state.current = {
        contextKey: null,
        generation: state.current.generation + 1,
      };
      state.byKey = {};
      state.disabledKeys = {};
    },
    accessWithdrawn(state, action) {
      const contextKey = action.payload;
      const entry = state.byKey[contextKey];
      if (!entry || contextKey !== state.current.contextKey) return;
      withdraw(state, contextKey, entry);
    },
    /**
     * FE3: record or replace the active capture for the CURRENT key. A record
     * for any other key is dropped, so a callback that outlived a context
     * change can never attach a capture to the new level.
     */
    captureRecorded(state, action) {
      const { contextKey, capture } = action.payload ?? {};
      if (!contextKey || contextKey !== state.current.contextKey) return;
      const entry = state.byKey[contextKey];
      if (!entry || entry.status !== "enabled") return;
      entry.capture = capture ?? null;
    },
    /**
     * FE3: clear the active capture on commit success, Start over, expiry or
     * any refusal that ends it. Scoped to the capture id when one is given,
     * so a late clear for an older capture never drops a newer one.
     */
    captureCleared(state, action) {
      const { contextKey, captureId = null } = action.payload ?? {};
      if (!contextKey || contextKey !== state.current.contextKey) return;
      const entry = state.byKey[contextKey];
      if (!entry?.capture) return;
      if (captureId && entry.capture.captureId !== captureId) return;
      entry.capture = null;
    },
    /** Retry for related files that failed to hydrate. */
    hydrationErrorsCleared(state) {
      const entry = state.byKey[state.current.contextKey];
      if (entry) entry.files.hydrationErrors = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(bootstrapWorkspace.pending, (state, action) => {
        const entry = claimSlot(state, action, opKeys.bootstrap());
        if (!entry) return;
        entry.lastValidationAttemptAt = action.meta.arg.startedAt ?? null;
        entry.bootstrapStartedAt = action.meta.arg.startedAt ?? null;
      })
      .addCase(bootstrapWorkspace.fulfilled, (state, action) => {
        const entry = owningEntry(state, action, opKeys.bootstrap());
        if (!entry) return;
        const data = action.payload.data ?? {};
        const firstEnable = entry.status !== "enabled";
        entry.status = "enabled";
        entry.error = null;
        entry.validatedAt = action.payload.settledAt;
        entry.consecutiveFailures = 0;
        entry.context = data.context ?? null;
        entry.capabilities = data.capabilities ?? null;
        entry.google = sanitizeGoogle(data.google);
        entry.outputs = Array.isArray(data.outputs) ? data.outputs : [];
        entry.submission = data.submission ?? null;
        /* FE3 plan 4.3: A PREVIEW AGAINST AN OLDER SUBMISSION IS STALE. The
           server would refuse its commit with `submission_changed`; the
           active capture goes now, so nothing offers to submit it. */
        if (
          entry.capture &&
          entry.capture.expectedSubmissionVersion !==
            (entry.submission?.version ?? null)
        ) {
          entry.capture = null;
        }
        if (firstEnable) {
          entry.tasks = freshEntry().tasks;
          entry.files = freshEntry().files;
          entry.references = freshEntry().references;
          mergePage(entry.tasks, data.tasks);
          entry.tasks.nextCursor = data.tasks_next_cursor ?? null;
          entry.tasks.complete = entry.tasks.nextCursor === null;
          mergePage(entry.files, data.files?.items);
          entry.files.nextCursor = data.files?.next_cursor ?? null;
          mergePage(entry.references, data.references?.items);
          entry.references.nextCursor = data.references?.next_cursor ?? null;
          return;
        }
        /* A REVALIDATION upserts what it saw and leaves traversal state alone:
           its first pages do not say anything about rows beyond them. */
        mergePage(entry.tasks, data.tasks);
        mergePage(entry.files, data.files?.items);
        mergePage(entry.references, data.references?.items);
      })
      .addCase(bootstrapWorkspace.rejected, (state, action) => {
        const entry = owningEntry(state, action, opKeys.bootstrap());
        if (!entry) return;
        const error = action.payload ?? normalizeError(action.error);
        if (error.aborted) return;
        if (error.code === "workspace_disabled") {
          if (entry.status === "enabled") {
            withdraw(state, action.meta.arg.contextKey, entry);
          } else {
            clearProtected(entry);
            entry.status = "disabled";
            state.disabledKeys[action.meta.arg.contextKey] = true;
          }
          return;
        }
        entry.consecutiveFailures += 1;
        if (isContextWide(error)) {
          protect(entry, error);
          return;
        }
        /* A transient failure on a REVALIDATION keeps the workspace the
           learner already has; the bounded revalidation backs off. A transient
           failure on the FIRST bootstrap is a protected state with Retry. */
        if (entry.status === "enabled") return;
        entry.status = "error";
        entry.error = { ...error, kind: "unavailable" };
      })

      .addCase(loadPage.pending, (state, action) => {
        const entry = claimSlot(state, action, opKeys.page(action.meta.arg));
        if (!entry) return;
        const collection = entry[action.meta.arg.collection];
        if (collection) collection.error = null;
      })
      .addCase(loadPage.fulfilled, (state, action) => {
        const entry = owningEntry(state, action, opKeys.page(action.meta.arg));
        if (!entry) return;
        const { collection: name, auto } = action.meta.arg;
        const collection = entry[name];
        const data = action.payload.data ?? {};
        mergePage(collection, data.items);
        collection.nextCursor = data.next_cursor ?? null;
        collection.error = null;
        if (name === "tasks") {
          collection.complete = collection.nextCursor === null;
          if (auto) collection.autoFollowed += 1;
        }
        if (name === "references") reconcileRetained(entry);
      })
      .addCase(loadPage.rejected, (state, action) => {
        const entry = owningEntry(state, action, opKeys.page(action.meta.arg));
        if (!entry) return;
        if (applyContextFailure(state, action, entry)) return;
        const error = action.payload ?? normalizeError(action.error);
        entry[action.meta.arg.collection].error = error;
      })

      .addCase(refreshEntity.pending, (state, action) => {
        const entry = claimSlot(state, action, opKeys.entity(action.meta.arg));
        if (!entry) return;
        if (action.meta.arg.collection === "files") {
          delete entry.files.hydrationErrors[Number(action.meta.arg.id)];
        }
      })
      .addCase(refreshEntity.fulfilled, (state, action) => {
        const entry = owningEntry(
          state,
          action,
          opKeys.entity(action.meta.arg),
        );
        if (!entry) return;
        entry.appliedReads[opKeys.entity(action.meta.arg)] =
          action.meta.requestId;
        const { collection: name } = action.meta.arg;
        const id = Number(action.meta.arg.id);
        const item = action.payload.data?.item ?? null;
        if (item) {
          upsert(entry[name], item);
          if (name === "files") delete entry.files.retained[id];
          return;
        }
        /* NO LONGER VISIBLE IN THIS CONTEXT. Removed from the cache, and a
           vanished file is removed from every task that linked it, so no
           output group waits for a file that will never arrive. */
        removeFrom(entry[name], id);
        if (name === "files") {
          delete entry.files.hydrationErrors[id];
          /* An unloaded references page may still supply this id from an
             earlier level. Keep the link and show the group as partial; the
             hydration error stops an automatic re-read, and loading that
             page resolves the link. */
          if (entry.references.nextCursor !== null) {
            entry.files.hydrationErrors[id] = true;
            entry.files.retained[id] = true;
            return;
          }
          Object.values(entry.tasks.byId).forEach((task) => {
            if (!Array.isArray(task.file_ids)) return;
            task.file_ids = task.file_ids.filter(
              (fileId) => Number(fileId) !== id,
            );
          });
        }
      })
      .addCase(refreshEntity.rejected, (state, action) => {
        const entry = owningEntry(
          state,
          action,
          opKeys.entity(action.meta.arg),
        );
        if (!entry) return;
        if (applyContextFailure(state, action, entry)) return;
        if (action.meta.arg.collection === "files") {
          entry.files.hydrationErrors[Number(action.meta.arg.id)] = true;
        }
      })

      .addCase(runMutation.pending, (state, action) => {
        claimSlot(state, action, opKeys.mutation(action.meta.arg));
      })
      .addCase(runMutation.fulfilled, (state, action) => {
        /* A receipt describes an operation, not current state. Release the
           slot and write nothing. */
        owningEntry(state, action, opKeys.mutation(action.meta.arg));
      })
      .addCase(runMutation.rejected, (state, action) => {
        const entry = owningEntry(
          state,
          action,
          opKeys.mutation(action.meta.arg),
        );
        if (!entry) return;
        const error = action.payload ?? normalizeError(action.error);
        /* Only a withdrawal is decided here. Every other refusal of a
           mutation is about that one row or action (plan 4.6), and the caller
           handles it without touching the rest of the workspace. */
        if (error.code === "workspace_disabled") {
          withdraw(state, action.meta.arg.contextKey, entry);
        }
      })

      .addCase(loadEarlierFiles.pending, (state, action) => {
        const entry = claimSlot(state, action, opKeys.earlier(action.meta.arg));
        if (!entry) return;
        const id = Number(action.meta.arg.earlierCapsuleId);
        entry.earlier[id] = {
          ...(entry.earlier[id] ?? emptyCollection()),
          status: "loading",
          error: null,
        };
      })
      .addCase(loadEarlierFiles.fulfilled, (state, action) => {
        const entry = owningEntry(
          state,
          action,
          opKeys.earlier(action.meta.arg),
        );
        if (!entry) return;
        const id = Number(action.meta.arg.earlierCapsuleId);
        const level = entry.earlier[id] ?? emptyCollection();
        const data = action.payload.data ?? {};
        mergePage(level, data.items);
        level.nextCursor = data.next_cursor ?? null;
        level.status = "loaded";
        level.error = null;
        entry.earlier[id] = level;
      })
      .addCase(loadEarlierFiles.rejected, (state, action) => {
        const entry = owningEntry(
          state,
          action,
          opKeys.earlier(action.meta.arg),
        );
        if (!entry) return;
        const error = action.payload ?? normalizeError(action.error);
        if (error.aborted) return;
        /* The cohort flag covers every level, so a disabled answer here is a
           withdrawal. Any other refusal is about THAT earlier level (a
           credential lock on level 2 says nothing about level 3), so it stays
           on that level's row with Retry. */
        if (error.code === "workspace_disabled") {
          withdraw(state, action.meta.arg.contextKey, entry);
          return;
        }
        const id = Number(action.meta.arg.earlierCapsuleId);
        entry.earlier[id] = {
          ...(entry.earlier[id] ?? emptyCollection()),
          status: "error",
          error,
        };
      });
  },
});

export const {
  contextActivated,
  contextCleared,
  sessionEnded,
  accessWithdrawn,
  hydrationErrorsCleared,
  captureRecorded,
  captureCleared,
} = workspaceSlice.actions;

export const WORKSPACE_OP_KEYS = opKeys;

export default workspaceSlice.reducer;
